import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from '@/components/ui/pagination';
import { Loader2, Search, Pencil } from 'lucide-react';
import * as publicBlogApi from '@/api/publicBlogApi';
import { Blog, BlogCategory } from '@/types/blog';
import { format } from 'date-fns';
import BlogCTA from '@/components/BlogCTA';
import FrequentlyAskedQuestions from '@/components/shared/FrequentlyAskedQuestions';
import ProvenResults from '@/components/shared/ProvenResults';
import SEO from '@/components/shared/SEO';

const BlogListing = () => {
  const { slug } = useParams(); // Get slug from URL params
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalBlogs, setTotalBlogs] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const pageSize = 6;

  // Fetch categories and find the category ID for the provided slug
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesData = await publicBlogApi.getPublicCategories();
        setCategories(categoriesData || []);
        
        // If we have a category slug in the URL, find its ID
        if (slug) {
          const category = categoriesData?.find(cat => cat.slug === slug);
          if (category) {
            setSelectedCategory(category.id);
          }
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    
    fetchCategories();
  }, [slug]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch blogs with category filter if selected and sort descending
        const response = await publicBlogApi.getPublicBlogs(currentPage, pageSize, selectedCategory, 'desc');
        
        // Set blogs from direct Supabase query
        const validatedBlogs = response.data?.map(blog => ({
          ...blog,
          categories: blog.categories || [] // Ensure categories is always an array
        })) || [];
        setBlogs(validatedBlogs);
        setTotalBlogs(response.pagination?.totalItems || 0);
      } catch (error) {
        console.error('Error fetching blog data:', error);
        setBlogs([]);
        setTotalBlogs(0);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    // Reset to page 1 when category changes
    if (currentPage !== 1 && selectedCategory !== null) {
      setCurrentPage(1);
    }
  }, [currentPage, selectedCategory]);
  
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real implementation, you would add search functionality
    console.log('Search for:', searchTerm);
  };
  
  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMMM dd, yyyy');
  };
  
  const totalPages = Math.ceil(totalBlogs / pageSize);

  // Create blog listing structured data
  const blogListingSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "RoundOne AI Blog",
    "description": "Latest insights and updates on recruitment, AI technology, and hiring best practices",
    "url": "https://roundone.ai/blog",
    "publisher": {
      "@type": "Organization",
      "name": "RoundOne AI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://roundone.ai/images/dark-logo.png"
      }
    }
  };

  // Create breadcrumb structured data
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://roundone.ai"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://roundone.ai/blog"
      }
    ]
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO 
        title="Blog - RoundOne AI"
        description="Stay updated with the latest insights on recruitment, AI technology, and hiring best practices. Read our expert articles and guides."
        url="/blog"
        schema={[blogListingSchema, breadcrumbSchema]}
      />

      <Header />
      
      <main className="flex-1">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">RoundOne AI Blog</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Insights, news, and updates from the RoundOne team on recruitment, AI technology, and more.
            </p>
          </div>
          
          {/* Categories as capsules */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            <Button
              variant="outline"
              size="sm"
              className={`rounded-full text-xs py-1 px-4 h-7 font-medium transition-all ${
                selectedCategory === null 
                  ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white border-transparent hover:from-purple-600 hover:to-indigo-700'
                  : 'bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700'
              }`}
              onClick={() => setSelectedCategory(null)}
            >
              ALL
            </Button>
            
            {categories.map(category => (
              <Button
                key={category.id}
                variant="outline"
                size="sm"
                className={`rounded-full text-xs py-1 px-4 h-7 font-medium transition-all ${
                  selectedCategory === category.id 
                    ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white border-transparent hover:from-purple-600 hover:to-indigo-700'
                    : 'bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700'
                }`}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.name.toUpperCase()}
              </Button>
            ))}
          </div>
          
          {/* Search input centered */}
          <div className="max-w-md mx-auto mb-12">
            <form onSubmit={handleSearch}>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search blog posts..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </form>
          </div>
          
          {/* Blog Posts */}
          <div className="w-full">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
              </div>
            ) : blogs.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="text-xl font-medium mb-2">No blog posts found</h3>
                <p className="text-muted-foreground">
                  Check back soon for new content.
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                  {blogs.map(blog => (
                    <Card key={blog.id} className="h-full flex flex-col">
                      <CardContent className="p-0 flex-grow">
                        {blog.featured_image && (
                          <Link to={`/blog/${blog.slug}`} className="block">
                            <div className="relative h-48 overflow-hidden">
                              <img
                                src={blog.featured_image}
                                alt={blog.title}
                                className="w-full h-full object-cover transition-transform hover:scale-105"
                              />
                            </div>
                          </Link>
                        )}
                        
                        <div className="p-6">
                          <div className="flex items-center text-xs text-muted-foreground mb-3">
                            <time dateTime={blog.published_at || blog.created_at}>
                              {formatDate(blog.published_at || blog.created_at)}
                            </time>
                            
                            {blog.categories && blog.categories.length > 0 && (
                              <>
                                <span className="mx-2">•</span>
                                <div className="flex flex-wrap gap-1">
                                  {blog.categories.slice(0, 1).map(category => (
                                    category && (
                                      <Link
                                        key={category.id}
                                        to={`/blog/category/${category.slug}`}
                                        className="hover:text-primary transition-colors"
                                      >
                                        {category.name}
                                      </Link>
                                    )
                                  ))}
                                  {blog.categories.length > 1 && (
                                    <span>+{blog.categories.length - 1}</span>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                          
                          <Link to={`/blog/${blog.slug}`} className="block group">
                            <h2 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                              {blog.title}
                            </h2>
                          </Link>
                          
                          {blog.excerpt && (
                            <p className="text-muted-foreground line-clamp-3 mb-4">
                              {blog.excerpt}
                            </p>
                          )}
                          
                          <div className="flex gap-2">
                            <Button asChild variant="ghost" size="sm">
                              <Link to={`/blog/${blog.slug}`}>
                                Read More
                              </Link>
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                {totalPages > 1 && (
                  <Pagination className="mt-8 flex justify-center">
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          className={`bg-zinc-800 text-white hover:bg-purple-500 ${currentPage <= 1 ? "pointer-events-none opacity-50" : ""}`}
                        />
                      </PaginationItem>
                      
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <PaginationItem key={page}>
                          <PaginationLink
                            onClick={() => setCurrentPage(page)}
                            isActive={currentPage === page}
                            className={`${currentPage === page ? 'bg-purple-600 text-white' : 'bg-zinc-800 text-white'} hover:bg-purple-500 hover:text-white`}
                          >
                            {page}
                          </PaginationLink>
                        </PaginationItem>
                      ))}
                      
                      <PaginationItem>
                        <PaginationNext
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          className={`bg-zinc-800 text-white hover:bg-purple-500 ${currentPage >= totalPages ? "pointer-events-none opacity-50" : ""}`}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                )}
              </>
            )}
          </div>
        </div>

        {/* Add FrequentlyAskedQuestions component */}
        <FrequentlyAskedQuestions />
        
        {/* Add ProvenResults component */}
        <ProvenResults />
      </main>
      
      <Footer />
    </div>
  );
};

export default BlogListing; 